import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import {
  ScanBarcode,
  Layers,
  Package,
  BarChart3,
  Calendar,
  Truck,
  History,
  ClipboardList,
  Bell,
  TrendingUp,
  Sparkles,
  FileText,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import type { DashboardStats } from './types';

interface QuickActionsGridProps {
  stats: DashboardStats;
  onOpenScanner: () => void;
  onOpenBatchScan?: () => void;
}

interface QuickAction {
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  label: string;
  sublabel: string;
  to: string;
  gradient?: string;
  alertCondition?: (stats: DashboardStats) => boolean;
}

const quickActions: QuickAction[] = [
  {
    icon: Package,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    label: 'Produits',
    sublabel: 'Catalogue',
    to: '/products',
  },
  {
    icon: BarChart3,
    iconColor: 'text-green-600',
    iconBg: 'bg-green-100 dark:bg-green-900/50',
    label: 'Stocks',
    sublabel: 'Inventaire',
    to: '/inventory',
  },
  {
    icon: Calendar,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    label: 'Lots & DLC',
    sublabel: 'Traçabilité',
    to: '/lots',
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
  },
  {
    icon: Truck,
    iconColor: 'text-cyan-600',
    iconBg: 'bg-cyan-100 dark:bg-cyan-900/50',
    label: 'Fournisseurs',
    sublabel: 'Contacts',
    to: '/suppliers',
  },
  {
    icon: History,
    iconColor: 'text-slate-600',
    iconBg: 'bg-slate-100 dark:bg-slate-900/50',
    label: 'Historique',
    sublabel: 'Mouvements',
    to: '/history',
  },
  {
    icon: ClipboardList,
    iconColor: 'text-orange-600',
    iconBg: 'bg-orange-100 dark:bg-orange-900/50',
    label: 'Inventaire',
    sublabel: 'Mode comptage',
    to: '/inventory-mode',
    gradient: 'from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30',
  },
  {
    icon: Bell,
    iconColor: 'text-red-600',
    iconBg: 'bg-red-100 dark:bg-red-900/50',
    label: 'Alertes',
    sublabel: 'OK',
    to: '/alerts',
    alertCondition: (s) => s.criticalStocks > 0 || s.warningStocks > 0,
  },
  {
    icon: TrendingUp,
    iconColor: 'text-indigo-600',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    label: 'Analytics',
    sublabel: 'Tendances',
    to: '/analytics',
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30',
  },
  {
    icon: Sparkles,
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100 dark:bg-purple-900/50',
    label: 'Insights IA',
    sublabel: 'Suggestions',
    to: '/insights',
    gradient: 'from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30',
  },
  {
    icon: FileText,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    label: 'Rapports',
    sublabel: 'Exports',
    to: '/reports',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
  },
  {
    icon: Settings,
    iconColor: 'text-gray-600',
    iconBg: 'bg-gray-100 dark:bg-gray-700',
    label: 'Paramètres',
    sublabel: 'Configuration',
    to: '/settings',
  },
];

export function QuickActionsGrid({ stats, onOpenScanner, onOpenBatchScan }: QuickActionsGridProps) {
  return (
    <div>
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
        Actions rapides
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
        {/* Scanner - First position */}
        <button
          onClick={onOpenScanner}
          className="w-full text-left p-3 sm:p-4 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm transition-all duration-200 hover:shadow-lg cursor-pointer hover:scale-[1.02]"
        >
          <div className="text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-2">
              <ScanBarcode size={24} className="text-white" />
            </div>
            <h3 className="font-semibold text-white text-xs sm:text-sm">Scanner</h3>
            <p className="text-xs text-blue-100 hidden sm:block mt-1">Code-barres</p>
          </div>
        </button>

        {/* Batch Scan */}
        {onOpenBatchScan && (
          <button
            onClick={onOpenBatchScan}
            className="w-full text-left p-3 sm:p-4 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-sm transition-all duration-200 hover:shadow-lg cursor-pointer hover:scale-[1.02]"
          >
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Layers size={24} className="text-white" />
              </div>
              <h3 className="font-semibold text-white text-xs sm:text-sm">Scan batch</h3>
              <p className="text-xs text-teal-100 hidden sm:block mt-1">Multi-produits</p>
            </div>
          </button>
        )}

        {/* All actions with icons */}
        {quickActions.map((action) => {
          const Icon = action.icon;
          const isAlert = action.alertCondition?.(stats);
          const alertSublabel =
            action.to === '/alerts' && isAlert
              ? `${stats.criticalStocks + stats.warningStocks} à voir`
              : action.sublabel;

          return (
            <Link key={action.to} to={action.to}>
              <Card
                padding="sm"
                hoverable
                className={`h-full rounded-xl ${
                  isAlert
                    ? 'border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30'
                    : action.gradient
                    ? `border border-transparent hover:border-primary bg-gradient-to-br ${action.gradient}`
                    : 'border border-gray-200 dark:border-gray-700 hover:border-primary'
                }`}
              >
                <div className="text-center py-1">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 ${action.iconBg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                    <Icon size={20} className={`${action.iconColor} sm:w-6 sm:h-6`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">
                    {action.label}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 hidden sm:block mt-1">
                    {alertSublabel}
                  </p>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
